import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import type { Segment } from "@repo/ui/types";
import type { FlightFareBundles } from "@repo/ui/types/cms/flight";

import { useAppSelector } from "../hooks";

export interface FlightSelection {
  journeyKey: string;
  fareAvailabilityKey: string;
  // Selected pack/bundle code (e.g., "packSmart", "packFull", "free")
  memberFare?: number;
  memberFareInclusiveTax?: number;
  dcFare?: number;
  dcFareInclusiveTax?: number;
  segments?: number;
  segmentKeys?: Array<string>;
  bundleCode: string;
  bundleName?: string;
  bundleTaxTotal: number;
  bundlePriceTotal: number;
  promotionDiscountAmount?: number;
  min_fare_price_outbound: number;
  min_fare_price_inbound: number;
  fullSegments: Segment[];
}

export interface FlightState {
  outbound: FlightSelection;
  inbound: FlightSelection;
  // Whether inbound selection is required (true for round-trip)
  taxesIncluded: boolean;
  inboundRequired?: boolean;
  confirmed: boolean;
  flightBundles: FlightFareBundles["bundles"];
}

const jourenyInitialState: FlightSelection = {
  fareAvailabilityKey: "",
  journeyKey: "",
  memberFare: 0,
  memberFareInclusiveTax: 0,
  dcFare: 0,
  segments: 0,
  segmentKeys: [],
  bundleCode: "",
  bundleName: "",
  dcFareInclusiveTax: 0,
  bundleTaxTotal: 0,
  bundlePriceTotal: 0,
  min_fare_price_outbound: 0,
  min_fare_price_inbound: 0,
  fullSegments: [],
};

const initialState: FlightState = {
  outbound: {
    ...jourenyInitialState,
  },
  inbound: {
    ...jourenyInitialState,
  },
  taxesIncluded: false,
  inboundRequired: false,
  confirmed: false,
  flightBundles: [],
};

const flightSlice = createSlice({
  name: "flight",
  initialState,
  reducers: {
    // Update only the journey details after clicking the blue price button
    setJourney: (
      state,
      action: PayloadAction<{
        direction: "outbound" | "inbound";
        journeyKey: string;
        fareAvailabilityKey: string;
        memberFare: number;
        memberFareInclusiveTax: number;
        dcFare: number;
        dcFareInclusiveTax: number;
        segments?: number;
        fullSegments?: Segment[];
        segmentKeys?: Array<string>;
        bundlePriceTotal?: number;
        bundleTaxTotal?: number;
      }>,
    ) => {
      const { direction, ...rest } = action.payload;
      const current = state[direction];
      state[direction] = {
        ...current,
        ...rest,
      };
      // Preserve the confirmed state when syncing journey prices
    },
    // Update only the bundle/prices after clicking "I want it!"
    setBundle: (
      state,
      action: PayloadAction<{
        direction: "outbound" | "inbound";
        bundleCode: string;
        bundleName: string;
        bundlePriceTotal: number;
        bundleTaxTotal: number;
      }>,
    ) => {
      const { direction, ...rest } = action.payload;
      const current = state[direction];
      state[direction] = {
        ...current,
        ...rest,
      };
      state.confirmed = false;
    },
    removeJourney: (state, action: PayloadAction<"outbound" | "inbound">) => {
      const direction = action.payload;
      state[direction] = {
        ...state[direction],
        ...jourenyInitialState,
      };
      state.confirmed = false;
    },
    removeBundle: (state, action: PayloadAction<"outbound" | "inbound">) => {
      const direction = action.payload;
      state[direction] = {
        ...state[direction],
        ...{
          bundleCode: "",
          bundleTaxTotal: 0,
          bundlePriceTotal: 0,
        },
      };
      state.confirmed = false;
    },
    setInboundRequired: (state, action: PayloadAction<boolean>) => {
      state.inboundRequired = action.payload;
    },
    confirmSelections: (state) => {
      state.confirmed = true;
    },
    resetJourney: () => initialState,
    setMinFarePrices: (
      state,
      action: PayloadAction<{
        min_fare_price_outbound: number;
        min_fare_price_inbound: number;
      }>,
    ) => {
      const { min_fare_price_outbound, min_fare_price_inbound } = action.payload;

      state.outbound.min_fare_price_outbound = min_fare_price_outbound;
      state.inbound.min_fare_price_inbound = min_fare_price_inbound;
    },
    setFlightBundles: (state, action: PayloadAction<FlightFareBundles["bundles"]>) => {
      state.flightBundles = action.payload;
    },
  },
});

// Hooks
export const useFlightState = () => useAppSelector((state) => state.flight);
export const useOutboundSelection = () => useAppSelector((state) => state.flight.outbound);
export const useInboundSelection = () => useAppSelector((state) => state.flight.inbound);
export const useJourneyKey = (direction: "outbound" | "inbound") =>
  useAppSelector((state) => state.flight[direction].journeyKey);
export const useBundleCode = (direction: "outbound" | "inbound") =>
  useAppSelector((state) => state.flight[direction].bundleCode);
export const useFlightConfirmed = () => useAppSelector((state) => state.flight.confirmed);
export const useInboundRequired = () =>
  useAppSelector((state) => state.flight.inboundRequired ?? false);
export const useMinFarePrices = () =>
  useAppSelector((state) => ({
    min_fare_price_outbound: state.flight.outbound.min_fare_price_outbound,
    min_fare_price_inbound: state.flight.inbound.min_fare_price_inbound,
  }));
export const useJourneySelection = (direction: "outbound" | "inbound") =>
  useAppSelector((state) => state.flight[direction]);
export const useFlightBundles = () => useAppSelector((state) => state.flight.flightBundles);
export const useFlightBundleByCode = (bundleCode: string) =>
  useAppSelector((state) => state.flight.flightBundles.find((b) => b.bundleCode === bundleCode));

export const useFreeBundle = () =>
  useAppSelector((state) =>
    state.flight.flightBundles.find(
      (b) => b.id.toLowerCase() === "basic" || b.id.toLowerCase() === "free",
    ),
  );

export const {
  setJourney,
  setBundle,
  removeBundle,
  removeJourney,
  resetJourney,
  setInboundRequired,
  confirmSelections,
  setMinFarePrices,
  setFlightBundles,
} = flightSlice.actions;
export default flightSlice.reducer;
